import { useEffect, useState } from "react";
import { Bell, Mail, Send, Loader2, CheckCircle2, AlertTriangle, Clock, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { hasPerm } from "../lib/permissions";

const ACCENT = "#EC4899";

export default function NotificationSettings() {
  const { user } = useAuth();
  const canSubscribe = hasPerm(user, "ncr_alerts", "subscribe");
  const [config, setConfig] = useState(null);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState(null);

  const [email, setEmail] = useState("");
  const [depts, setDepts] = useState([]);
  const [dailyDigest, setDailyDigest] = useState(true);
  const [instantAlerts, setInstantAlerts] = useState(true);
  const [active, setActive] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [c, s] = await Promise.all([
          api.get("/ncr-alerts/config"),
          api.get("/ncr-alerts/my-subscription"),
        ]);
        if (cancelled) return;
        setConfig(c.data);
        const existing = s.data?.subscription;
        setSub(existing || null);
        setEmail(existing?.email || user?.email || "");
        setDepts(existing?.depts || ["MOLD", "FRAME"]);
        setDailyDigest(existing?.daily_digest ?? true);
        setInstantAlerts(existing?.instant_alerts ?? true);
        setActive(existing?.active ?? true);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.email]);

  const toggleDept = (d) => {
    setDepts((curr) => curr.includes(d) ? curr.filter((x) => x !== d) : [...curr, d]);
  };

  const handleSave = async () => {
    if (!email) { toast.error("Email is required"); return; }
    if (depts.length === 0) { toast.error("Pick at least one department"); return; }
    setSaving(true);
    try {
      const r = await api.put("/ncr-alerts/my-subscription", {
        email, depts, daily_digest: dailyDigest, instant_alerts: instantAlerts, active,
      });
      setSub(r.data.subscription);
      toast.success("Subscription saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!sub) return;
    if (!window.confirm("Unsubscribe from NCR SLA alerts?")) return;
    try {
      await api.delete("/ncr-alerts/my-subscription");
      setSub(null);
      toast.success("Unsubscribed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/ncr-alerts/test-email");
      if (r.data.status === "sent") {
        toast.success(`Test email sent to ${r.data.to}`);
      } else if (r.data.status === "not_configured") {
        toast.warning("Email service is not configured yet. Ask admin to add RESEND_API_KEY in backend/.env to enable delivery.");
      } else {
        toast.error(`Test failed: ${r.data.reason || r.data.status}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const handlePreview = async () => {
    try {
      const r = await api.get("/ncr-alerts/preview-digest");
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!canSubscribe) {
    return (
      <div className="space-y-6" data-testid="notifications-page">
        <PageHeader />
        <div className="bg-[#12141D] border border-border p-8 text-center" data-testid="notif-no-perm">
          <ShieldAlert size={32} className="text-amber-400 mx-auto mb-3" />
          <h2 className="font-heading font-bold text-xl">Permission required</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Your role doesn&apos;t include <span className="font-mono text-white">ncr_alerts.subscribe</span> yet.
            Ask an administrator to grant the NCR SLA Alerts permission to enable email alerts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <PageHeader />

      {!config?.configured && (
        <div className="bg-amber-950/30 border border-amber-700/40 px-5 py-3 flex items-start gap-3 text-sm" data-testid="notif-config-banner">
          <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
          <div>
            <div className="text-amber-200 font-medium">Email service not configured</div>
            <div className="text-amber-200/70 text-xs mt-0.5">
              Add <span className="font-mono">RESEND_API_KEY</span> in <span className="font-mono">backend/.env</span> to start sending. Subscriptions are stored and will activate as soon as the key is set.
            </div>
          </div>
        </div>
      )}

      <section className="bg-[#12141D] border border-border overflow-hidden" data-testid="notif-form-card">
        <span className="block h-[2px]" style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />
        <div className="p-6 space-y-5">
          <div className="flex items-start gap-3">
            <Bell size={20} style={{ color: ACCENT }} />
            <div>
              <h3 className="font-heading font-bold text-xl">NCR SLA Alert Subscription</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Get notified when open NCRs cross the {config?.sla_hours || 72}-hour SLA. Daily digest at {String(config?.digest_hour_utc ?? 8).padStart(2, "0")}:00 UTC, instant alerts every ~{Math.round((config?.poll_seconds || 900) / 60)} min.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="py-8 flex items-center justify-center text-muted-foreground text-xs uppercase tracking-widest">
              <Loader2 size={14} className="animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <>
              <Field label="Delivery email">
                <input
                  data-testid="notif-email-input"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#0A0B10] border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#EC4899]/60"
                  placeholder="you@company.com"
                />
              </Field>

              <Field label="Departments">
                <div className="flex flex-wrap gap-2" data-testid="notif-dept-chips">
                  {(config?.supported_depts || ["MOLD", "FRAME"]).map((d) => {
                    const on = depts.includes(d);
                    return (
                      <button
                        key={d}
                        data-testid={`notif-dept-${d.toLowerCase()}`}
                        type="button"
                        onClick={() => toggleDept(d)}
                        className={`px-3 py-1.5 text-[11px] tracking-widest uppercase border transition-colors ${
                          on ? "bg-[#EC4899] text-[#0A0B10] border-[#EC4899]" : "bg-[#0A0B10] border-border text-muted-foreground hover:text-white"
                        }`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <ToggleRow
                  testId="notif-toggle-digest"
                  icon={<Clock size={14} />}
                  label="Daily digest"
                  sub="Once a day at 08:00 UTC"
                  on={dailyDigest} onChange={setDailyDigest}
                />
                <ToggleRow
                  testId="notif-toggle-instant"
                  icon={<AlertTriangle size={14} />}
                  label="Instant alerts"
                  sub="When an NCR crosses 72h"
                  on={instantAlerts} onChange={setInstantAlerts}
                />
                <ToggleRow
                  testId="notif-toggle-active"
                  icon={<CheckCircle2 size={14} />}
                  label="Subscription active"
                  sub="Pause without losing settings"
                  on={active} onChange={setActive}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/40">
                <button
                  data-testid="notif-save-btn"
                  onClick={handleSave} disabled={saving}
                  className="bg-[#EC4899] hover:bg-[#EC4899]/90 text-[#0A0B10] font-semibold text-xs uppercase tracking-widest px-4 py-2 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 size={12} className="animate-spin" />}
                  {sub ? "Update subscription" : "Subscribe"}
                </button>
                <button
                  data-testid="notif-test-btn"
                  onClick={handleTest} disabled={!sub || testing}
                  className="bg-[#0A0B10] border border-border hover:border-[#EC4899]/60 text-xs uppercase tracking-widest px-4 py-2 transition-colors disabled:opacity-40 flex items-center gap-2"
                  title={!sub ? "Save subscription first" : "Send a test email to your address"}
                >
                  {testing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Send test now
                </button>
                <button
                  data-testid="notif-preview-btn"
                  onClick={handlePreview}
                  className="bg-[#0A0B10] border border-border hover:border-[#EC4899]/60 text-xs uppercase tracking-widest px-4 py-2 transition-colors flex items-center gap-2"
                  title="Preview what today's digest would contain"
                >
                  <Mail size={12} /> Preview digest
                </button>
                {sub && (
                  <button
                    data-testid="notif-unsubscribe-btn"
                    onClick={handleUnsubscribe}
                    className="ml-auto text-xs uppercase tracking-widest text-muted-foreground hover:text-red-400 transition-colors"
                  >
                    Unsubscribe
                  </button>
                )}
              </div>

              {sub && (
                <div className="text-[11px] text-muted-foreground font-mono pt-2 border-t border-border/40" data-testid="notif-status">
                  Last digest: {sub.last_digest_at ? new Date(sub.last_digest_at).toLocaleString() : "—"}
                  {"  ·  "}
                  Last instant: {sub.last_instant_at ? new Date(sub.last_instant_at).toLocaleString() : "—"}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {preview && (
        <section className="bg-[#12141D] border border-border p-6" data-testid="notif-preview-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-lg">Digest preview</h3>
            <span className="text-[10px] tracking-widest uppercase text-muted-foreground font-mono">
              {preview.total} total breaches
            </span>
          </div>
          {preview.total === 0 ? (
            <div className="text-sm text-emerald-400">No SLA breaches in your selected departments. ✓</div>
          ) : (
            <div className="space-y-3">
              {Object.entries(preview.breaches_by_dept || {}).map(([d, rows]) => (
                <div key={d}>
                  <div className="text-[11px] tracking-widest uppercase mb-1" style={{ color: ACCENT }}>
                    {d} · {rows.length} breached
                  </div>
                  <ul className="space-y-1 text-xs">
                    {rows.slice(0, 5).map((r) => (
                      <li key={r.key} className="font-mono flex items-center gap-2">
                        <span className="text-red-400 tabular-nums">{r.age_days >= 1 ? `${r.age_days}d` : `${r.age_hours}h`}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-white">{r.job}</span>
                        <span className="text-muted-foreground">/ {r.piece_mark}</span>
                        <span className="text-muted-foreground truncate flex-1">— {r.problem}</span>
                      </li>
                    ))}
                    {rows.length > 5 && (
                      <li className="text-[11px] text-muted-foreground italic">+ {rows.length - 5} more</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function PageHeader() {
  return (
    <header>
      <div className="text-[11px] tracking-[0.35em] uppercase text-[#00D4FF]">09 · Notifications</div>
      <h1 className="font-heading font-bold text-4xl tracking-tight mt-2">NCR SLA Alerts</h1>
      <p className="text-sm text-muted-foreground mt-1">
        Subscribe to email alerts so SLA breaches don&apos;t slip past your team leads
      </p>
    </header>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function ToggleRow({ testId, icon, label, sub, on, onChange }) {
  return (
    <label
      data-testid={testId}
      onClick={() => onChange(!on)}
      className="bg-[#0A0B10] border border-border p-3 flex items-start gap-3 cursor-pointer hover:border-[#EC4899]/60 transition-colors"
    >
      <span className="mt-0.5" style={{ color: ACCENT }}>{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-[11px] text-muted-foreground">{sub}</div>
      </div>
      <span
        className="relative inline-flex h-5 w-9 items-center transition-colors shrink-0"
        style={{ backgroundColor: on ? ACCENT : "#1F2937" }}
      >
        <span
          className="inline-block h-3 w-3 bg-white transition-transform"
          style={{ transform: on ? "translateX(20px)" : "translateX(4px)" }}
        />
      </span>
    </label>
  );
}
